/**
 * BeCoder three-experience architecture helpers.
 * Visitor (public) · Customer (Google) · Admin (password) — never mixed.
 *
 * Guests browse the public storefront freely. Google Sign-In is only for
 * account-dependent actions (trial, subscribe, launch, portal, etc.).
 */

export type Experience = 'public' | 'customer' | 'admin';

/** Paths that represent an account-dependent action (not public browse). */
const ACCOUNT_ACTION_PREFIXES = ['/start-trial', '/customer'] as const;

/**
 * Public commercial/storefront prefixes — must remain reachable without auth.
 * Matching is by path prefix (see `isPublicStorefrontPath`).
 */
export const PUBLIC_STOREFRONT_PREFIXES = [
  '/',
  '/applications',
  '/products',
  '/apps',
  '/marketplace',
  '/docs',
  '/kb',
  '/status',
  '/about',
  '/contact',
  '/pricing',
  '/start-trial',
] as const;

/** Customer workspace prefixes — require Google customer session. */
export const CUSTOMER_WORKSPACE_PREFIXES = ['/customer'] as const;

/** Admin console prefixes — require platform_admin session. */
export const ADMIN_CONSOLE_PREFIXES = ['/admin'] as const;

/**
 * Paths allowed as post-login resume targets (including public pages that
 * continue an interrupted action, e.g. `/applications/:slug?action=launch`).
 * Browse remains public — these are resume destinations, not browse gates.
 */
const CUSTOMER_RETURN_PREFIXES = [
  '/',
  '/customer',
  '/start-trial',
  '/applications',
  '/products',
  '/apps',
  '/pricing',
  '/docs',
  '/kb',
  '/status',
  '/about',
  '/contact',
  '/marketplace',
] as const;

function isSafeRelativePath(raw: string): boolean {
  return (
    raw.startsWith('/') &&
    !raw.startsWith('//') &&
    !raw.includes('://') &&
    !raw.includes('..') &&
    !raw.includes('\\')
  );
}

function pathMatchesPrefix(path: string, prefix: string): boolean {
  if (prefix === '/') return path === '/';
  return (
    path === prefix ||
    path.startsWith(`${prefix}/`) ||
    path.startsWith(`${prefix}?`)
  );
}

/** Safe post-auth destination for customer Google sign-in resume. */
export function resolveCustomerReturnTo(
  next: string | null | undefined,
  fallback = '/customer'
): string {
  if (!next) return fallback;
  const raw = next.trim();
  if (!isSafeRelativePath(raw)) return fallback;
  if (raw === '/admin' || raw.startsWith('/admin/') || raw.startsWith('/admin?')) {
    return fallback;
  }
  if (raw === '/login' || raw.startsWith('/login')) return fallback;
  if (raw === '/admin/login') return fallback;

  const pathOnly = raw.split(/[?#]/)[0] || raw;
  const allowed = CUSTOMER_RETURN_PREFIXES.some((prefix) =>
    pathMatchesPrefix(pathOnly === '/' ? '/' : pathOnly, prefix) ||
    (prefix !== '/' && pathMatchesPrefix(raw, prefix))
  );
  if (!allowed) return fallback;
  return raw;
}

export function customerSignInPath(next?: string | null): string {
  if (!next) return '/login';
  const safe = resolveCustomerReturnTo(next, '');
  if (!safe) return '/login';
  return `/login?next=${encodeURIComponent(safe)}`;
}

export function adminSignInPath(next?: string | null): string {
  if (!next || !next.startsWith('/admin')) return '/admin/login';
  return `/admin/login?next=${encodeURIComponent(next)}`;
}

/** True when the path is an account action (trial / customer portal), not public browse. */
export function isAccountActionPath(pathname: string): boolean {
  const path = pathname.split(/[?#]/)[0] || pathname;
  return ACCOUNT_ACTION_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`));
}

/** True when the path is part of the anonymous-browsable commercial storefront. */
export function isPublicStorefrontPath(pathname: string): boolean {
  const path = pathname.split(/[?#]/)[0] || pathname;
  if (path === '/login' || path.startsWith('/login')) return false;
  if (ADMIN_CONSOLE_PREFIXES.some((p) => pathMatchesPrefix(path, p))) return false;
  if (CUSTOMER_WORKSPACE_PREFIXES.some((p) => pathMatchesPrefix(path, p))) return false;
  return PUBLIC_STOREFRONT_PREFIXES.some((p) => pathMatchesPrefix(path, p));
}

/** True when the path belongs to the private customer workspace. */
export function isCustomerWorkspacePath(pathname: string): boolean {
  const path = pathname.split(/[?#]/)[0] || pathname;
  return CUSTOMER_WORKSPACE_PREFIXES.some((p) => pathMatchesPrefix(path, p));
}

/** True when the path belongs to the admin console (including /admin/login). */
export function isAdminConsolePath(pathname: string): boolean {
  const path = pathname.split(/[?#]/)[0] || pathname;
  return ADMIN_CONSOLE_PREFIXES.some((p) => pathMatchesPrefix(path, p));
}

/**
 * Destination after Google (or already-authenticated customer login resume).
 * Welcome is only used when onboarding is needed AND the intended destination
 * is the bare customer home / welcome — never hijack trial/subscribe/launch.
 */
export function resolveCustomerPostAuthDestination(options: {
  returnTo: string;
  needsOnboarding?: boolean;
  isAdmin?: boolean;
}): string {
  if (options.isAdmin) return '/admin';
  const destination = resolveCustomerReturnTo(options.returnTo, '/customer');
  if (
    options.needsOnboarding &&
    (destination === '/customer' || destination.startsWith('/customer/welcome'))
  ) {
    return '/customer/welcome';
  }
  return destination;
}
